import { print } from 'graphql';
import { jwtDecode } from 'jwt-decode';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { CONFIG } from '@frameio/config';
import { enqueueOrRun } from '@frameio/segment-ot';
import { abortable } from '../../async/abort';
import { sleep } from '../../async/sleep';
import { isDesktop } from '../../config/helpers';
import { hash } from '../../encode/hash';
import { isUUID } from '../../formatters';
import { appUtilsStorage } from '../../storage/appUtilsStorage';
import {
  ANONYMOUS_USER,
  CLIENT_SIDE_ACCESS_TOKEN_EXPIRATION_BUFFER_SECONDS,
  CREDENTIAL_KEYS,
} from '../constants';
import { TransientError } from '../fetchNewAccessToken';
import { getLoginUrl } from '../getLoginUrl';
import { hasAccessTokenExpired } from '../hasAccessTokenExpired';
import type { AccessTokens, AuthState, FetchedAccessTokens, Hash } from '.';

const { USER_ID } = CREDENTIAL_KEYS;

const restrictedRedirectPaths = ['/logout', '/auth/refresh'];

/* eslint-disable no-await-in-loop, no-continue */

// a LucidChart diagram of how we use Auth for token refresh can be found at
// https://lucid.app/lucidchart/a56c227f-bb78-4313-990a-ce9afad216c0/edit?invitationId=inv_4bc3602d-a933-43cf-9ce9-1c92e51f7303

export type HandleAuthCallbackOpts = {
  skipInboxRedirect?: boolean;
};

export abstract class Auth {
  async hash() {
    return (await this.state()).hash;
  }

  #state?: AuthState = undefined;

  state = async () => {
    const current = this.getTokens();
    if (this.#state) {
      const state = this.#state;
      const { tokens } = state;
      if (
        current.access === tokens.access &&
        current.session === tokens.session &&
        current.refresh === tokens.refresh &&
        current.share === tokens.share
      ) {
        return state;
      }
    }

    this.setState({
      tokens: current,
      hash: await hash(current),
    });
    return this.#state!;
  };

  getState(): AuthState | null {
    return this.#state || null;
  }

  protected subscribers = new Set<(state: AuthState) => void>();

  setState(state: AuthState) {
    this.#state = state;
    for (const subscriber of this.subscribers) {
      try {
        subscriber(state);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error occurred in auth state listener:', err);
      }
    }
  }

  subscribe(subscriber: (state: AuthState) => void) {
    this.addSubscriber(subscriber);
    return () => this.removeSubscriber(subscriber);
  }

  protected addSubscriber(subscriber: (state: AuthState) => void) {
    this.subscribers.add(subscriber);
    this.state().then((state) => subscriber(state));
  }

  protected removeSubscriber(subscriber: (state: AuthState) => void) {
    this.subscribers.delete(subscriber);
  }

  async *states(signal?: AbortSignal) {
    let resolve: ((state: AuthState) => void) | null = null;
    const unsub = this.subscribe((state) => {
      if (resolve) {
        resolve(state);
        resolve = null;
      }
    });
    let lastState: AuthState | null = null;
    try {
      while (true) {
        const nextState = await abortable(
          // eslint-disable-next-line no-await-in-loop, no-loop-func
          new Promise<AuthState>((r) => {
            resolve = r;
          }),
          signal
        );
        if (lastState && nextState.hash === lastState.hash) {
          continue;
        }
        lastState = nextState;
        yield nextState;
      }
    } finally {
      unsub();
    }
  }

  /**
   * Construct an Auth Portal login URL that will redirect back to the
   * user's current URL when they are authenticated.
   *
   * @returns The constructed login url the user should be redirected to
   */
  public getLoginUrl = (currentUrl: string): string => {
    return getLoginUrl(currentUrl, {
      authCallbackPath: CONFIG.authCallbackPath,
      authPortalOrigin: CONFIG.authPortalOrigin,
    });
  };

  getRefreshUrl() {
    const url = new URL('/auth/refresh', 'https://example.com');
    const params = new URLSearchParams(window.location.search);
    const then =
      window.location.pathname + window.location.search + window.location.hash;
    url.searchParams.set('then', then);
    const state = this.getState();
    if (state?.hash) {
      url.searchParams.set('state', state.hash);
    }
    if (params.has('test.auth.refreshDelay')) {
      url.searchParams.set(
        'test.auth.refreshDelay',
        params.get('test.auth.refreshDelay')!
      );
    }
    if (params.has('test.auth.refreshError')) {
      url.searchParams.set(
        'test.auth.refreshError',
        params.get('test.auth.refreshError')!
      );
    }
    return `${url.pathname}${url.search}`;
  }

  /**
   * Get the access token from the user's cookies.
   *
   * @environments client
   * @returns the access token if the user is authenticated
   */
  abstract getAccessToken(): string | undefined;

  /**
   * Get the share token for the current runtime environment
   *
   * @environments client
   * @returns the share token if it exists
   */
  abstract getShareToken(): string | undefined;

  /**
   * Get all the tokens needed to perform authorization from a client/worker.
   * Allows us to conveniently pass this information through to a worker that may not share the same runtime environment
   *
   * @environment client
   * @returns a list of tokens necessary to conduct long-running authentication
   */
  public getTokens = (known?: AccessTokens): AccessTokens => {
    return {
      access: this.getAccessToken(),
      session: this.getSessionToken(),
      refresh: this.getRefreshToken(),
      share: this.getShareToken(),
      ...(known || {}),
    };
  };

  /**
   * Sets tokens to recreate a state provided by getTokens (getTokens often having been executed within another runtime environment)
   * Allows us to conveniently pass information between runtime environments, required to maintain a consistent auth experience
   *
   * @environment client
   */
  setTokens(known: AccessTokens) {
    if (known.access) this.setAccessToken(known.access);
    if (known.session) this.setSessionToken(known.session);
    if (known.refresh) this.setRefreshToken(known.refresh);
    if (known.share) this.setShareToken(known.share);
  }

  abstract setShareToken(shareToken: string): void;

  /**
   * Get the refresh token from the user's cookies. On the client side, this is
   * cached on an instance variable. On the server, we always get the cookie value.
   *
   * @environments client
   * @param [serverSideCookieHeader] On the server, pass in the cookie string from the request header
   *  for use when instantiating universal-cookie
   * @returns the refresh token if the user is authenticated
   */
  abstract getRefreshToken(): string | undefined;

  /**
   * Get the user session token from the user's cookies.
   *
   * @environments client
   * @returns the user session token if the user is authenticated
   */
  abstract getSessionToken(): string | undefined;

  /**
   * Get the userId from the user's localStorage
   *
   * @environments client
   * @returns userId
   */
  public getUserId = (): string => {
    // if null return anonymous user.
    try {
      return appUtilsStorage.get(CREDENTIAL_KEYS.USER_ID) || ANONYMOUS_USER;
    } catch {
      return ANONYMOUS_USER;
    }
  };

  /**
   * Util that returns if the user is a returning previously authenticated
   * Frame.io user in this browser. Prefers the localStorage userId, but falls
   * back to the auth cookies, which are the source of truth, because the
   * localStorage userId is only written when an auth callback includes it and is
   * wiped on logout / storage clears, so it can be absent for a user who is
   * genuinely authenticated.
   * @environments client
   * @returns boolean
   */
  public hasEverBeenAuthenticatedUser = (): boolean => {
    if (isUUID(this.getUserId())) return true;

    const accessToken = this.getAccessToken();
    if (accessToken && isUUID(this.getAccessTokenUserId(accessToken))) {
      return true;
    }

    return Boolean(this.getRefreshToken() || this.getSessionToken());
  };

  /**
   * Remove all user credentials from cookies and storage
   *
   * @environments client
   */
  private logoutClearStoredData = () => {
    // Remove V4 localStorage
    appUtilsStorage.remove(USER_ID);

    this.removeTokens();
  };

  /**
   * Clears user credentials from cookies and storage and returns a login
   * URL to optionally redirect the user to.
   *
   * @environments client
   * @returns {string} The login URL to optionally redirect the user to.
   */
  public logout = (redirectPath: string = '/'): string => {
    this.logoutClearStoredData();

    const redirectUrl = new URL(window.location.href);
    redirectUrl.pathname = redirectPath;

    return this.getLoginUrl(redirectUrl.toString());
  };

  /**
   * Clears user credentials from cookies and storage and returns a login
   * URL to optionally redirect the user to which includes the current pathname.
   *
   * @environments client
   * @returns {string} The login URL to optionally redirect the user to.
   */
  public logoutWithPersistedRedirectPath = (): string => {
    this.logoutClearStoredData();
    return this.getRedirectUrl();
  };

  getRedirectUrl(href = window.location.href): string {
    const redirectUrl = new URL(href, window.location.href);
    if (restrictedRedirectPaths.includes(redirectUrl.pathname)) {
      redirectUrl.pathname = '/';
    }

    return this.getLoginUrl(redirectUrl.toString());
  }

  avoidRedirectToAuthPortal = false;

  /**
   * Log out the user and redirect to the auth portal
   *
   * @environments client
   */
  public redirectToAuthPortal = (redirectUrl?: string): void => {
    if (
      this.avoidRedirectToAuthPortal ||
      CONFIG.avoidRedirectToAuthPortalOnLogout
    ) {
      return;
    }

    // In the desktop app, we navigate to the logout page, which clears the
    // authentication tokens and directs the user to the desktop login page.
    // Directly redirecting to the auth portal is not desired in this scenario.
    if (isDesktop()) {
      window.location.replace('/logout');
      return;
    }

    // Track doorslam event before redirecting
    const url = this.getRedirectUrl(redirectUrl);
    enqueueOrRun({
      event: () =>
        window.analytics?.track('doorslam', {
          type: 'logout',
          from: window.location.href,
          to: url,
        }),
    });
    window.location.replace(url);
  };

  redirectToAuthRefresh = (): void => {
    if (
      this.avoidRedirectToAuthPortal ||
      CONFIG.avoidRedirectToAuthPortalOnLogout
    ) {
      return;
    }

    const url = this.getRefreshUrl();
    enqueueOrRun({
      event: () =>
        window.analytics?.track('refresh-redirect', {
          type: 'refresh',
          from: window.location.href,
          to: url,
        }),
    });
    window.location.replace(url);
  };

  /**
   * Stores the userId in localstorage
   * @todo (WEB-790): Storing the userId locally is a remnant from the design of the
   * original JT Frame.io app. The userId is already encoded in the accessToken so
   * we shouldn't need to store this. Consider deprecating.
   * @param userId The user Id string
   */
  public setUserId = (userId?: string): void => {
    appUtilsStorage.set(USER_ID, userId);
  };

  /**
   * Set the JWT access token cookie and instance variable.
   *
   * @environments client
   * @param [accessToken] the access token to set
   */
  abstract setAccessToken(accessToken: string): void;

  /**
   * Remove the JWT access and refresh tokens from cookies
   *
   * @environments client
   */
  private removeTokens = (): void => {
    this.removeSessionToken();
    this.removeRefreshToken();
    this.removeAccessToken();
    this.removeShareToken();
  };

  abstract removeAccessToken(): void;

  abstract removeRefreshToken(): void;

  abstract removeSessionToken(): void;

  abstract removeShareToken(): void;

  /**
   * Set the refresh token.
   *
   * @environments client
   * @param [refreshToken] the refresh token to set
   */
  abstract setRefreshToken(refreshToken?: string): void;

  /**
   * Set the session token.
   *
   * @environments client
   * @param [sessionToken] the session token to set
   */
  abstract setSessionToken(sessionToken: string): void;

  /**
   * Handle the refresh of the auth state.
   *
   * Implementations should block until a state other than {state} is acquired
   * through whatever means, then set and return it.
   *
   * @param state - the current state or hash
   * @param signal - an optional abort signal to cancel the operation
   * @returns a new auth state
   */
  abstract handleRefresh(
    state: Hash<AccessTokens>,
    options: { signal?: AbortSignal }
  ): Promise<AuthState>;

  /**
   * Refresh the auth state, requesting new tokens if needed.
   *
   * Always
   *
   * To avoid a race condition, refresh is synchronized across
   * tabs using a navigator lock. If {state} is provided, refresh
   * will return without refreshing if the state has updated since
   * it was called.
   *
   * @param state - the current state or hash
   * @returns the new auth state
   */
  async refresh(
    state?: AuthState | Hash<AccessTokens>,
    options: { signal?: AbortSignal } = {}
  ): Promise<AuthState> {
    const opts = {
      ...options,
    };
    if (!state) {
      // eslint-disable-next-line no-param-reassign
      state = await this.state();
    }
    const startHash = typeof state! === 'string' ? state : state.hash;
    return this.handleRefresh(startHash, opts);
  }

  public async handleAuthCallback(
    __: string,
    ___?: HandleAuthCallbackOpts
  ): Promise<string> {
    throw new Error('Only implemented on window.');
  }

  /**
   * Ensure that the access token is valid for at least {seconds},
   * refreshing if need be, and retrying on transient errors.
   *
   * @param seconds - the minimum number of seconds the access token should be valid for
   * @param signal - an optional abort signal to cancel the operation
   * @param state - an optional auth hash. if set, will always try to refresh until
   *                the state is changed from the given hash
   * @returns the current auth state
   */
  ensure(
    options: {
      signal?: AbortSignal;
      seconds?: number;
      state?: Hash<AccessTokens>;
    } = {}
  ): Ensure {
    return new Ensure(this, options);
  }

  /**
   * This method is for getting the access token, with a refresh of
   * that token if it is expired or not set. This method should be
   * called async, and is the preferred method for retrieving the
   * access token.
   *
   * @environments client, worker
   * @returns the access token
   */
  public getAccessTokenWithRefreshIfExpired = async (
    seconds = CLIENT_SIDE_ACCESS_TOKEN_EXPIRATION_BUFFER_SECONDS,
    signal?: AbortSignal
  ): Promise<string | undefined> => {
    try {
      const state = await this.ensure({
        seconds,
        signal,
      });
      return state.tokens.access;
    } catch (error) {
      this.redirectToAuthRefresh();
      return this.getAccessToken();
    }
  };

  /**
   * This method is for getting the user id from the access token
   *
   * @environments client
   * @param [accessToken] the access token to pull the user ID from
   * @returns the access token
   */
  public getAccessTokenUserId = (accessToken: string): string => {
    try {
      const jwtToken = jwtDecode(accessToken);
      return jwtToken.sub!.replace('User:', '');
    } catch {
      return '';
    }
  };

  /**
   * Fetch the refreshed access token from the backend.
   *
   * Note: this is only implemented in windowAuth, since only from the window can we
   * update the document cookies. The method is declared here for testing.
   *
   * @param refreshToken the refresh token to use, overriding the default
   * @returns the fetched access tokens
   */
  protected async fetchAccessToken(
    __refreshToken?: string
  ): Promise<FetchedAccessTokens> {
    throw new Error('Not implemented');
  }

  protected async query<T, V>({
    query,
    variables,
    signal,
    operationName,
  }: {
    query: TypedDocumentNode<T, V> | string;
    variables?: V;
    signal?: AbortSignal;
    operationName: string;
  }): Promise<{ data?: T; errors?: any[] }> {
    const clientName = CONFIG.thirdParty.apolloClient?.name;
    const clientVersion = CONFIG.clientIdentifierVersion;

    const fetchResponse = await fetch(CONFIG.graphqlUrl, {
      method: 'POST',
      signal,
      headers: {
        authorization: `Bearer ${this.getAccessToken()}`,
        'Content-Type': 'application/json',
        'apollographql-client-name': clientName!,
        'apollographql-client-version': clientVersion!,
        'x-gql-op': operationName,
      } as const,
      body: JSON.stringify({
        operationName,
        query: typeof query === 'string' ? query : print(query),
        variables,
      }),
    });

    return fetchResponse.json();
  }
}

export type InvalidStatus = { is: 'invalid'; state: AuthState };
export type ReadyStatus = { is: 'ready'; state: AuthState };
export type ErrorStatus = { is: 'error'; error: any; willRetry?: number };
export type RetryStatus = { is: 'retry'; count: number };
export type EnsureStatus =
  | InvalidStatus
  | RetryStatus
  | ReadyStatus
  | ErrorStatus;

class Ensure implements Promise<AuthState>, AsyncIterable<EnsureStatus> {
  constructor(
    public readonly auth: Auth,
    public readonly options: {
      signal?: AbortSignal;
      seconds?: number;
      state?: Hash<AccessTokens>;
    }
  ) {
    this.options = {
      ...options,
      seconds:
        options.seconds ?? CLIENT_SIDE_ACCESS_TOKEN_EXPIRATION_BUFFER_SECONDS,
    };
  }

  async *[Symbol.asyncIterator](): AsyncIterator<EnsureStatus> {
    const { options } = this;
    let nextSleep = 100;
    let state = await this.auth.state();
    let retries = 0;
    while (
      (options.state && state.hash === options.state) ||
      hasAccessTokenExpired(state.tokens.access, options.seconds)
    ) {
      yield { is: 'invalid', state };
      options.signal?.throwIfAborted();
      try {
        state = await this.auth.refresh(
          options.state ? options.state : state,
          options
        );
      } catch (error) {
        if (error instanceof TransientError) {
          yield { is: 'error', error, willRetry: nextSleep };
          await sleep(nextSleep);
          nextSleep = Math.min(nextSleep * 2, 16_000);
          // eslint-disable-next-line no-plusplus
          yield { is: 'retry', count: ++retries };
          continue;
        }
        yield { is: 'error', error };
        return;
      } finally {
        state = await this.auth.state();
      }
    }
    yield { is: 'ready', state };
  }

  async promise(): Promise<AuthState> {
    let lastStatus: EnsureStatus | null = null;
    for await (const status of this) {
      if (status.is === 'ready') {
        return status.state;
      }
      lastStatus = status;
    }
    if (lastStatus?.is === 'error') {
      throw lastStatus.error;
    } else {
      return this.auth.state();
    }
  }

  async then<TResult1 = AuthState, TResult2 = never>(
    onfulfilled?:
      | ((value: AuthState) => TResult1 | PromiseLike<TResult1>)
      | undefined
      | null,
    onrejected?:
      | ((reason: any) => TResult2 | PromiseLike<TResult2>)
      | undefined
      | null
  ): Promise<TResult1 | TResult2> {
    return this.promise().then(onfulfilled, onrejected);
  }

  catch<TResult = never>(
    onrejected?:
      | ((reason: any) => TResult | PromiseLike<TResult>)
      | undefined
      | null
  ): Promise<AuthState | TResult> {
    return this.promise().catch(onrejected);
  }

  finally(onfinally?: (() => void) | undefined | null): Promise<AuthState> {
    return this.promise().finally(onfinally);
  }

  [Symbol.toStringTag] = 'Ensure';
}
